import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "react-toastify";
import useBhandarewala from "../../../services/Order/UseBhandarewala";

const CREATE_ORDER_USER_ID = "A1437";
const DEFAULT_RESTAURANT_NUMBER = "918587052590";
const BULK_ORDER_URL = "https://catalog.anlook.com/bulk-food/setup";

const getTenDigitMobile = (value) => {
  let digits = String(value ?? "").replace(/\D/g, "");

  if (digits.startsWith("0091") && digits.length >= 14) {
    digits = digits.slice(4);
  } else if (digits.startsWith("091") && digits.length >= 13) {
    digits = digits.slice(3);
  } else if (digits.startsWith("91") && digits.length >= 12) {
    digits = digits.slice(2);
  }

  if (digits.length === 11 && digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const getUrlMobile = (value) => {
  const mobile = getTenDigitMobile(value);

  return /^\d{10}$/.test(mobile) ? `91${mobile}` : mobile;
};

const getRestaurantNumberFromReceipt = (receipt = {}) => {
  const rawNumber =
    receipt?.phone_no ||
    receipt?.phoneNo ||
    receipt?.phone ||
    receipt?.mobile ||
    receipt?.mobile_no ||
    receipt?.mobileNo ||
    receipt?.restaurant_mobile ||
    receipt?.organization_mobile ||
    receipt?.shop_mobile ||
    receipt?.contact_number ||
    receipt?.contactNo ||
    receipt?.contact_no ||
    receipt?.business_mobile ||
    "";

  const normalized = getUrlMobile(rawNumber);

  return /^91\d{10}$/.test(normalized) ? normalized : DEFAULT_RESTAURANT_NUMBER;
};

const CreateOrderModal = ({ onClose, onStartOrder, loading }) => {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
  });

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "mobile" ? getTenDigitMobile(value) : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const mobile = getTenDigitMobile(form.mobile);

    if (!name) {
      toast.error("Please enter customer name");
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Enter valid 10 digit mobile number");
      return;
    }

    onStartOrder({ name, mobile });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 montserrat-font-1"
      onClick={() => {
        if (!loading) {
          onClose();
        }
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              Create Order
            </h2>

            <p className="mt-0.5 text-xs text-gray-500">
              Enter customer details to start bulk order
            </p>
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">
              Name
            </span>

            <input
              type="text"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              placeholder="Enter customer name"
              disabled={loading}
              autoComplete="name"
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-gray-700">
              Mobile
            </span>

            <input
              type="tel"
              inputMode="numeric"
              value={form.mobile}
              onChange={(event) => updateField("mobile", event.target.value)}
              placeholder="9810964409"
              disabled={loading}
              autoComplete="tel"
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100"
            />

            <p className="mt-1 text-[11px] text-gray-500">
              Enter only 10 digit mobile number.
            </p>
          </label>
        </div>

        <div className="flex justify-end gap-3 border-t px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-10 min-w-[120px] items-center justify-center rounded-lg bg-green-500 px-4 text-sm font-semibold text-white transition hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Starting..." : "Start Order"}
          </button>
        </div>
      </form>
    </div>
  );
};

const CreateBulkOrder = ({ userID, receiptDetails }) => {
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [createOrderLoading, setCreateOrderLoading] = useState(false);

  const { generateBulkOrderNumber } = useBhandarewala();

  const handleStartOrder = async ({ name, mobile }) => {
    if (userID !== CREATE_ORDER_USER_ID) {
      toast.error("Create Order is not available for this user");
      return;
    }

    if (createOrderLoading) {
      return;
    }

    const customerMobile = getTenDigitMobile(mobile);

    if (!/^\d{10}$/.test(customerMobile)) {
      toast.error("Enter valid 10 digit mobile number");
      return;
    }

    const urlMobile = `91${customerMobile}`;
    const orderWindow = window.open("about:blank", "_blank");

    try {
      setCreateOrderLoading(true);

      const result = await generateBulkOrderNumber({
        userID,
        mobile: customerMobile,
      });

      if (!result?.success || !result?.orderId) {
        if (orderWindow && !orderWindow.closed) {
          orderWindow.close();
        }
        return;
      }

      const restaurantNumber = getRestaurantNumberFromReceipt(receiptDetails);
      const url = new URL(BULK_ORDER_URL);

      url.searchParams.set("mobile", urlMobile);
      url.searchParams.set("userid", userID);
      url.searchParams.set("orderid", result.orderId);
      url.searchParams.set("restaurantNumber", restaurantNumber);
      url.searchParams.set("orderType", "home_delivery");
      url.searchParams.set("orderFrom", "chaafo");
      url.searchParams.set("name", name.trim());
      url.searchParams.set("isReferred", "true");

      setShowCreateOrderModal(false);

      if (orderWindow && !orderWindow.closed) {
        orderWindow.opener = null;
        orderWindow.location.href = url.toString();
      } else {
        window.location.href = url.toString();
      }
    } catch (error) {
      if (orderWindow && !orderWindow.closed) {
        orderWindow.close();
      }

      console.error("Create order navigation error:", error);
      toast.error(error?.message || "Unable to start order");
    } finally {
      setCreateOrderLoading(false);
    }
  };

  if (userID !== CREATE_ORDER_USER_ID) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setShowCreateOrderModal(true)}
        className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-emerald-700 transition"
      >
        <Plus size={16} />
        Create Order
      </button>

      {showCreateOrderModal && (
        <CreateOrderModal
          onClose={() => {
            if (!createOrderLoading) {
              setShowCreateOrderModal(false);
            }
          }}
          onStartOrder={handleStartOrder}
          loading={createOrderLoading}
        />
      )}
    </>
  );
};

export default CreateBulkOrder;
